import React, { useCallback, useEffect, useState } from 'react';
import { PercentIcon, DollarSignIcon, UserIcon, CalendarIcon, ShoppingCartIcon, CheckCircleIcon, FileTextIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Pagination } from '../components/ui/Pagination';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useSortableData } from '../hooks/useSortableData';
import { supabaseAdmin } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { CommissionRecord, VendorCommissionConfig } from '../types';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
export function ComisionesPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [activeTab, setActiveTab] = useState<'registros' | 'config'>('registros');
  const [records, setRecords] = useState<CommissionRecord[]>([]);
  const [configs, setConfigs] = useState<VendorCommissionConfig[]>([]);
  const [localConfigs, setLocalConfigs] = useState<Record<string, {
    venta: string;
    cobro: string;
  }>>({});
  const [isLoading, setIsLoading] = useState(true);
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'venta' | 'cobro'>('all');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const activeVendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  useEffect(() => {
    loadData();
  }, [user]);
  // Auto-refresh when commission data changes in Supabase
  const loadDataCallback = useCallback(() => {
    loadData();
  }, []);
  useRealtimeRefresh(['commission_records', 'vendor_commissions'], loadDataCallback);
  useEffect(() => {
    if (isAdmin && users.length > 0) {
      const initialLocal: Record<string, {
        venta: string;
        cobro: string;
      }> = {};
      activeVendors.forEach(v => {
        const existing = configs.find(c => c.vendor_id === v.id);
        initialLocal[v.id] = {
          venta: existing ? String(existing.comision_venta_pct) : '0',
          cobro: existing ? String(existing.comision_cobro_pct) : '0'
        };
      });
      setLocalConfigs(initialLocal);
    }
  }, [users, configs, isAdmin]);
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load records
      let query = supabaseAdmin.from('commission_records').select('*').order('fecha', {
        ascending: false
      });
      if (!isAdmin && user) {
        query = query.eq('vendor_id', user.id);
      }
      const {
        data: recordsData
      } = await query;
      if (recordsData) setRecords(recordsData);
      // Load configs (admin only)
      if (isAdmin) {
        const {
          data: configsData
        } = await supabaseAdmin.from('vendor_commissions').select('*');
        if (configsData) {
          setConfigs(configsData);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveConfig = async (vendorId: string) => {
    setSavingId(vendorId);
    const venta = parseFloat(localConfigs[vendorId]?.venta || '0');
    const cobro = parseFloat(localConfigs[vendorId]?.cobro || '0');
    const {
      error
    } = await supabaseAdmin.from('vendor_commissions').upsert({
      vendor_id: vendorId,
      comision_venta_pct: venta,
      comision_cobro_pct: cobro,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'vendor_id'
    });
    if (!error) {
      const vendor = users.find(u => u.id === vendorId);
      addActivityLog('Editó comisiones', `Vendedor: ${vendor?.nombre || vendorId} | Venta: ${venta}% | Cobro: ${cobro}%`);
      setSavedId(vendorId);
      setTimeout(() => setSavedId(null), 2000);
      setConfigs(prev => {
        const existing = prev.find(c => c.vendor_id === vendorId);
        if (existing) {
          return prev.map(c => c.vendor_id === vendorId ? {
            ...c,
            comision_venta_pct: venta,
            comision_cobro_pct: cobro
          } : c);
        }
        return [...prev, {
          vendor_id: vendorId,
          comision_venta_pct: venta,
          comision_cobro_pct: cobro
        }];
      });
    }
    setSavingId(null);
  };
  // Filtering records
  const filteredRecords = records.filter(r => {
    if (dateFrom && r.fecha < dateFrom) return false;
    if (dateTo && r.fecha > dateTo + 'T23:59:59') return false;
    if (vendorFilter !== 'all' && r.vendor_id !== vendorFilter) return false;
    if (typeFilter !== 'all' && r.tipo !== typeFilter) return false;
    return true;
  });
  // Sorting records
  const {
    items: sortedRecords,
    requestSort,
    sortConfig
  } = useSortableData(filteredRecords, {
    fecha: r => new Date(r.fecha).getTime(),
    vendedor: r => r.vendor_nombre,
    cliente: r => r.cliente_nombre,
    tipo: r => r.tipo,
    montoBase: r => r.monto_base,
    porcentaje: r => r.porcentaje,
    comision: r => r.monto_comision
  });
  const finalRecords = sortConfig ? sortedRecords : [...sortedRecords].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  // Pagination
  const totalPages = Math.ceil(finalRecords.length / ITEMS_PER_PAGE);
  const paginatedRecords = finalRecords.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Stats
  const totalComisiones = filteredRecords.reduce((sum, r) => sum + r.monto_comision, 0);
  const comisionesVenta = filteredRecords.filter(r => r.tipo === 'venta').reduce((sum, r) => sum + r.monto_comision, 0);
  const comisionesCobro = filteredRecords.filter(r => r.tipo === 'cobro').reduce((sum, r) => sum + r.monto_comision, 0);
  const vendedoresConComision = new Set(filteredRecords.map(r => r.vendor_id)).size;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2
  }).format(amount);
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  return <div className="space-y-4 sm:space-y-6" data-id="element-1390" data-mp="a89kjm6y3kpx">
      <PageHeader title="Comisiones" subtitle="Gestión de comisiones por venta y cobro" icon={<PercentIcon className="w-6 h-6" data-id="element-1392" data-mp="2gupc6cb6hyni" />} data-id="element-1391" data-mp="56k9w7nmx92d" />
      

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-stagger-1" data-id="element-1393" data-mp="2gtxkvdom6n31">
        <StatsCard title="Total Comisiones" value={formatCurrency(totalComisiones)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1395" data-mp="2qtoc96hvolyw" />} variant="success" data-id="element-1394" data-mp="2lryznmf0c08p" />
        
        <StatsCard title="Por Ventas" value={formatCurrency(comisionesVenta)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-1397" data-mp="2urvte2g97pam" />} variant="primary" data-id="element-1396" data-mp="2uuqelw73de53" />
        
        <StatsCard title="Por Cobros" value={formatCurrency(comisionesCobro)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-1399" data-mp="233u0ks9ap3g8" />} variant="warning" data-id="element-1398" data-mp="1y5sia9jp185t" />
        
        <StatsCard title="Vendedores Activos" value={vendedoresConComision} icon={<UserIcon className="w-5 h-5" data-id="element-1401" data-mp="2bvchj5e2fnad" />} variant="default" data-id="element-1400" data-mp="274dxpvtvc72d" />
        
      </div>

      <Card padding="none" className="animate-stagger-2" data-id="element-1402" data-mp="2bxgqw2uqhei1">
        <div className="flex border-b border-slate-200 overflow-x-auto no-scrollbar" data-id="element-1403" data-mp="1fcermgn0jnw0">
          <button onClick={() => setActiveTab('registros')} className={`px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'registros' ? 'border-[#2952C4] text-[#2952C4] bg-blue-50/30' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-1404" data-mp="1je9tpncrq5au">
            
            Registros de Comisiones
          </button>
          {isAdmin && <button onClick={() => setActiveTab('config')} className={`px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'config' ? 'border-[#2952C4] text-[#2952C4] bg-blue-50/30' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-1405" data-mp="hq0vx7w6detn">
            
              Configuración por Vendedor
            </button>}
        </div>

        {activeTab === 'registros' && <div className="p-4 sm:p-6" data-id="element-1406" data-mp="10jo2fvk6rpvf">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-1407" data-mp="1ehm9efw6gk0w">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1409" data-mp="1t3a06jwvymsq" />} data-id="element-1408" data-mp="1agalcesyc6ut" />
            
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1411" data-mp="1y0s9ibetf419" />} data-id="element-1410" data-mp="1t1yw0izj3d13" />
            
              {isAdmin && <Select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} options={[{
            value: 'all',
            label: 'Todos los vendedores'
          }, ...activeVendors.map(v => ({
            value: v.id,
            label: v.nombre
          }))]} data-id="element-1412" data-mp="1k2lj1gc746bm" />}
              <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} options={[{
            value: 'all',
            label: 'Todos los tipos'
          }, {
            value: 'venta',
            label: 'Ventas'
          }, {
            value: 'cobro',
            label: 'Cobros'
          }]} data-id="element-1413" data-mp="1f523md3rhni3" />
            
            </div>

            {/* Table */}
            {isLoading ? <div className="p-8 sm:p-12 text-center" data-id="element-1414" data-mp="wchhdeikign9">
                <div className="w-8 h-8 border-3 border-slate-200 border-t-[#2952C4] rounded-full animate-spin mx-auto mb-4" data-id="element-1415" data-mp="2g9hbgyinrvgw" />
                <p className="text-slate-500" data-id="element-1416" data-mp="2kbbal2d7grey">Cargando registros...</p>
              </div> : filteredRecords.length === 0 ? <div className="p-8 sm:p-16 text-center border-2 border-dashed border-slate-200 rounded-2xl" data-id="element-1417" data-mp="26cblxupp0cis">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1418" data-mp="2bb62eq06tozq">
                  <FileTextIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-1419" data-mp="2z221m86z3mh0" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-1420" data-mp="3414tss1uty7k">
                  No hay registros
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1421" data-mp="2u8h1oaltkif6">
                  No se encontraron comisiones con los filtros aplicados
                </p>
              </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1422" data-mp="3hwqkr4rnes2m">
                {/* ── MOBILE CARD VIEW (< sm) ── */}
                <div className="sm:hidden divide-y divide-slate-100" data-id="element-1423" data-mp="1ba168zxo2n3k">
                  {paginatedRecords.map(record => <div key={record.id} className="p-3.5" data-id="element-1424" data-mp="16c9hufw8dj44">
                      <div className="flex items-start justify-between gap-2" data-id="element-1425" data-mp="169naag9cb3lw">
                        <div className="min-w-0 flex-1" data-id="element-1426" data-mp="1k2354ts37vbq">
                          <p className="font-semibold text-slate-900 text-sm" data-id="element-1427" data-mp="1g113y7em1974">
                            {record.cliente_nombre}
                          </p>
                          {isAdmin && <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-1428" data-mp="nclus1i5tl3p">
                              {record.vendor_nombre}
                            </p>}
                          <p className="text-xs text-slate-400 mt-0.5" data-id="element-1429" data-mp="11dogue95mvem">
                            {formatDate(record.fecha)}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right" data-id="element-1430" data-mp="2cq8q9hn1zmc3">
                          <p className="font-bold text-emerald-600 text-sm" data-id="element-1431" data-mp="27syuzlczgggc">
                            {formatCurrency(record.monto_comision)}
                          </p>
                          <div className="flex items-center gap-1.5 mt-1 justify-end" data-id="element-1432" data-mp="1ae45v2z9xtyd">
                            {record.tipo === 'venta' ? <Badge variant="info" size="sm" data-id="element-1433" data-mp="1efz7y9p14bd7">
                                Venta
                              </Badge> : <Badge variant="success" size="sm" data-id="element-1434" data-mp="1jf2bfppd29j2">
                                Cobro
                              </Badge>}
                            <span className="text-[10px] text-slate-500" data-id="element-1435" data-mp="mqe87zib0qtp">
                              {record.porcentaje}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-1.5 text-[11px] text-slate-400" data-id="element-1436" data-mp="10ghng6irnk9n">
                        Base: {formatCurrency(record.monto_base)}
                      </div>
                    </div>)}
                  {/* Mobile footer total */}
                  <div className="p-3.5 bg-slate-50" data-id="element-1437" data-mp="7y2koyinw2r5">
                    <div className="flex items-center justify-between" data-id="element-1438" data-mp="fvzv6k09ueu">
                      <span className="text-xs font-bold text-slate-700" data-id="element-1439" data-mp="drz2xhelbis">
                        Total en esta página:
                      </span>
                      <span className="font-bold text-emerald-700 text-sm" data-id="element-1440" data-mp="3mmb2rk301xyj">
                        {formatCurrency(paginatedRecords.reduce((s, r) => s + r.monto_comision, 0))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── DESKTOP TABLE (sm+) ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-1441" data-mp="38ud2zzv6hef1">
                  <table className="w-full" data-id="element-1442" data-mp="33w369ktt25rb">
                    <thead data-id="element-1443" data-mp="33us1pisjctz5">
                      <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1444" data-mp="2yy1l8uctjwd7">
                        <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1445" data-mp="2yvx7lkp4bizq" />
                    
                        {isAdmin && <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1446" data-mp="2pzfwuiko1n4i" />}
                        <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1447" data-mp="xuqxhl8hv5er" />
                    
                        <SortableTableHeader label="Tipo" sortKey="tipo" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1448" data-mp="p1e0a73edhmv" />
                    
                        <SortableTableHeader label="Monto Base" sortKey="montoBase" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1449" data-mp="f2y59t191o83" />
                    
                        <SortableTableHeader label="Porcentaje" sortKey="porcentaje" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1450" data-mp="667b5m69cjg5" />
                    
                        <SortableTableHeader label="Comisión" sortKey="comision" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1451" data-mp="2am2gcv8u5xz6" />
                    
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-id="element-1452" data-mp="2jgqp4vdmdxsq">
                      {paginatedRecords.map(record => <tr key={record.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1453" data-mp="2elt74yyd4e3p">
                    
                          <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1454" data-mp="2odpez1q93il4">
                            {formatDate(record.fecha)}
                          </td>
                          {isAdmin && <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1455" data-mp="32cp2g6211rfp">
                              {record.vendor_nombre}
                            </td>}
                          <td className="px-5 py-4 text-sm text-slate-700" data-id="element-1456" data-mp="180igcc7lhq8m">
                            {record.cliente_nombre}
                          </td>
                          <td className="px-5 py-4 text-center" data-id="element-1457" data-mp="332nckhl0bc51">
                            {record.tipo === 'venta' ? <Badge variant="info" size="sm" data-id="element-1458" data-mp="381qg1xlc9aaw">
                                Venta
                              </Badge> : <Badge variant="success" size="sm" data-id="element-1459" data-mp="3d132ih8asfcy">
                                Cobro
                              </Badge>}
                          </td>
                          <td className="px-5 py-4 text-right text-sm text-slate-600" data-id="element-1460" data-mp="3hxti73g6x8xu">
                            {formatCurrency(record.monto_base)}
                          </td>
                          <td className="px-5 py-4 text-center font-medium text-slate-700" data-id="element-1461" data-mp="3qyzxxab0mfq1">
                            {record.porcentaje}%
                          </td>
                          <td className="px-5 py-4 text-right font-bold text-emerald-600" data-id="element-1462" data-mp="4jyhg0cp5s3q">
                            {formatCurrency(record.monto_comision)}
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-1463" data-mp="djlmqx8jri22">
                      <tr data-id="element-1464" data-mp="1y5a5jqbbn37i">
                        <td colSpan={isAdmin ? 6 : 5} className="px-5 py-3 text-right text-sm font-bold text-slate-700" data-id="element-1465" data-mp="1y1e0b2lhphj6">
                      
                          Total en esta página:
                        </td>
                        <td className="px-5 py-3 text-right font-bold text-emerald-700" data-id="element-1466" data-mp="1k47l1hzz60yp">
                          {formatCurrency(paginatedRecords.reduce((s, r) => s + r.monto_comision, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="p-4 border-t border-slate-200" data-id="element-1467" data-mp="2814x5axiw8mf">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredRecords.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1468" data-mp="232timmwa99xv" />
              
                </div>
              </div>}
          </div>}

        {activeTab === 'config' && isAdmin && <div className="p-4 sm:p-6" data-id="element-1469" data-mp="2vpo1jfdgc198">
            <div className="mb-4 sm:mb-6" data-id="element-1470" data-mp="243nztu0dcpfz">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1" data-id="element-1471" data-mp="242lb9sqiqz38">
                Configuración de Porcentajes
              </h3>
              <p className="text-xs sm:text-sm text-slate-500" data-id="element-1472" data-mp="1v3rh090nuksu">
                Establece el porcentaje de comisión que recibirá cada vendedor
                por sus ventas y cobros.
              </p>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1473" data-mp="2mvgvxxaqiqrx">
              {/* ── MOBILE CONFIG CARDS (< sm) ── */}
              <div className="sm:hidden divide-y divide-slate-100" data-id="element-1474" data-mp="2rre9jvum5j10">
                {activeVendors.map(vendor => <div key={vendor.id} className="p-3.5" data-id="element-1475" data-mp="2dvtrt3qmf6v6">
                    <div className="flex items-center gap-2.5 mb-3" data-id="element-1476" data-mp="2irr8jb5sqf8h">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm" data-id="element-1477" data-mp="12eu0obg0ey2q">
                        {vendor.nombre.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900 text-sm" data-id="element-1478" data-mp="yvlht17vn113">
                        {vendor.nombre}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-1479" data-mp="18ukdzkbgh477">
                      <div data-id="element-1480" data-mp="18sq3z11q2whw">
                        <label className="text-[11px] font-semibold text-slate-500 uppercase mb-1 block" data-id="element-1481" data-mp="13vze5m048mja">
                          % Venta
                        </label>
                        <div className="relative" data-id="element-1482" data-mp="g42gqzxexx1p">
                          <input type="number" min="0" max="100" step="0.1" value={localConfigs[vendor.id]?.venta || ''} onChange={e => setLocalConfigs(prev => ({
                      ...prev,
                      [vendor.id]: {
                        ...prev[vendor.id],
                        venta: e.target.value
                      }
                    }))} className="w-full pr-7 pl-3 py-2 text-center text-sm font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4] focus:border-[#2952C4]" data-id="element-1483" data-mp="b7tuz3opn5lk" />
                      
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm" data-id="element-1484" data-mp="2g2xta5ptpkwb">
                            %
                          </span>
                        </div>
                      </div>
                      <div data-id="element-1485" data-mp="2q03lxie35jyn">
                        <label className="text-[11px] font-semibold text-slate-500 uppercase mb-1 block" data-id="element-1486" data-mp="2pysijjojye82">
                          % Cobro
                        </label>
                        <div className="relative" data-id="element-1487" data-mp="2ysdw4zozdpfw">
                          <input type="number" min="0" max="100" step="0.1" value={localConfigs[vendor.id]?.cobro || ''} onChange={e => setLocalConfigs(prev => ({
                      ...prev,
                      [vendor.id]: {
                        ...prev[vendor.id],
                        cobro: e.target.value
                      }
                    }))} className="w-full pr-7 pl-3 py-2 text-center text-sm font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4] focus:border-[#2952C4]" data-id="element-1488" data-mp="3dq69pmayw3rg" />
                      
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm" data-id="element-1489" data-mp="3v1v0aamy4n0c">
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2" data-id="element-1490" data-mp="379ej02b18z23">
                      {savedId === vendor.id && <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 animate-in fade-in slide-in-from-right-2" data-id="element-1491" data-mp="3bckmx3aoae5e">
                          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1492" data-mp="2xecun8ol010k" /> Guardado
                        </span>}
                      <Button size="sm" onClick={() => handleSaveConfig(vendor.id)} isLoading={savingId === vendor.id} data-id="element-1493" data-mp="vknaie88pwv5">
                    
                        Guardar
                      </Button>
                    </div>
                  </div>)}
                {activeVendors.length === 0 && <div className="p-8 text-center text-slate-500 text-sm" data-id="element-1494" data-mp="z0huwraty4um">
                    No hay vendedores activos en el sistema.
                  </div>}
              </div>

              {/* ── DESKTOP CONFIG TABLE (sm+) ── */}
              <div className="hidden sm:block overflow-x-auto" data-id="element-1495" data-mp="k5tonaxlv2dy">
                <table className="w-full text-sm" data-id="element-1496" data-mp="k7ocoy46bam9">
                  <thead data-id="element-1497" data-mp="1hs0sujpzysps">
                    <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1498" data-mp="1hsalyg6c9fzp">
                      <th className="px-5 py-4 text-left font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1499" data-mp="1cv0qojw9qa3y">
                        Vendedor
                      </th>
                      <th className="px-5 py-4 text-center font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1500" data-mp="134pium9grgrs">
                        % Comisión Venta
                      </th>
                      <th className="px-5 py-4 text-center font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1501" data-mp="1vsts1hr8ubz8">
                        % Comisión Cobro
                      </th>
                      <th className="px-5 py-4 text-right font-semibold text-slate-600 uppercase tracking-wider" data-id="element-1502" data-mp="243u47a9x2sup">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-1503" data-mp="2i0z7x3m5rfms">
                    {activeVendors.map(vendor => <tr key={vendor.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1504" data-mp="2n0bvjqkusqqf">
                    
                        <td className="px-5 py-4" data-id="element-1505" data-mp="2r1f55vcb5qj7">
                          <div className="flex items-center gap-3" data-id="element-1506" data-mp="2r041dviv4is3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold" data-id="element-1507" data-mp="2vtownft2mynl">
                              {vendor.nombre.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-slate-900" data-id="element-1508" data-mp="35p09fskx5vsp">
                              {vendor.nombre}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4" data-id="element-1509" data-mp="6cu11dqygloz">
                          <div className="flex justify-center" data-id="element-1510" data-mp="6c10wvmi4uft">
                            <div className="relative w-28" data-id="element-1511" data-mp="l28b6o2cv75f">
                              <input type="number" min="0" max="100" step="0.1" value={localConfigs[vendor.id]?.venta || ''} onChange={e => setLocalConfigs(prev => ({
                          ...prev,
                          [vendor.id]: {
                            ...prev[vendor.id],
                            venta: e.target.value
                          }
                        }))} className="w-full pr-8 pl-3 py-2 text-center text-sm font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4] focus:border-[#2952C4]" data-id="element-1512" data-mp="l3t5cckrco2r" />
                          
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold" data-id="element-1513" data-mp="1cuqz7jyv2ept">
                                %
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4" data-id="element-1514" data-mp="1lb83d3nfuvwx">
                          <div className="flex justify-center" data-id="element-1515" data-mp="16d5vwidadqae">
                            <div className="relative w-28" data-id="element-1516" data-mp="16f0jy5jutyip">
                              <input type="number" min="0" max="100" step="0.1" value={localConfigs[vendor.id]?.cobro || ''} onChange={e => setLocalConfigs(prev => ({
                          ...prev,
                          [vendor.id]: {
                            ...prev[vendor.id],
                            cobro: e.target.value
                          }
                        }))} className="w-full pr-8 pl-3 py-2 text-center text-sm font-medium border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4] focus:border-[#2952C4]" data-id="element-1517" data-mp="1bciid208wvcg" />
                          
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold" data-id="element-1518" data-mp="2mrnzhb14iwfl">
                                %
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right" data-id="element-1519" data-mp="2nlewl6kimh8x">
                          <div className="flex items-center justify-end gap-3" data-id="element-1520" data-mp="2in3niy21oarr">
                            {savedId === vendor.id && <span className="text-xs font-bold text-emerald-600 flex items-center gap-1 animate-in fade-in slide-in-from-right-2" data-id="element-1521" data-mp="2do0j9ftw28ku">
                                <CheckCircleIcon className="w-4 h-4" data-id="element-1522" data-mp="28vquw48f9gko" /> Guardado
                              </span>}
                            <Button size="sm" onClick={() => handleSaveConfig(vendor.id)} isLoading={savingId === vendor.id} data-id="element-1523" data-mp="23ygw3xz57sk6">
                          
                              Guardar
                            </Button>
                          </div>
                        </td>
                      </tr>)}
                    {activeVendors.length === 0 && <tr data-id="element-1524" data-mp="5gwypb905ih5">
                        <td colSpan={4} className="px-5 py-8 text-center text-slate-500" data-id="element-1525" data-mp="5fluxbfk4aq1">
                      
                          No hay vendedores activos en el sistema.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>}
      </Card>
    </div>;
}